import React from 'react'

const Arr = () => {
    return (
        <div className='container'>
            <h1>Arrays in C++ | Declare | Initialize | </h1>
            <h2>What is an Array?</h2>
            <p>An array is a data structure that stores an element of the same data type sequentially. A C++ array has a fixed-size. </p>
            <p>You can see an array as a collection of variables of a similar data type. Instead of declaring each variable and assigning it a value individually, you can declare one variable (the array) and add the values of the various variables to it. Each value-added to the array is identified by an index. </p>
            <h2>Why do we need arrays?</h2>
            <p>Arrays are very important in any programming language. They provide a more convenient way of storing variables or a collection of data of a similar data type together instead of storing them separately. Each value of the array will be accessed separately. </p>
            <h2>Declare an array in C++</h2>
            <p>Array declaration in C++ involves stating the type as well as the number of elements to be stored by the array. Syntax: </p>
            
            <pre className='output'>type array-Name [ array-Size ];</pre>
            <h3>Rules for declaring a single-dimension array in C++. </h3>
            <ul>
                <li><strong>Type:</strong> The type is the type of elements to be stored in the array, and it must be a valid C++ data type. </li>
                <li><strong>Array-Name:</strong> The array-Name is the name to be assigned to the array. </li>
                <li><strong>Array-Size:</strong> The array-Size is the number of elements to be stored in the array. It must be an integer and greater than 0. </li>
                </ul>
                <p>For example, you can create an array named age and store the ages of 5 students as follows: </p>
                <pre className='output'>int age[5];</pre>
                <p>The array age will store 5 integers representing the ages of different students. </p>
                <h2>Array Initialization</h2>
                <p>Array initialization is the process of assigning/storing elements to an array. The initialization can be done in a single statement or one by one. Note that the first element in an array is stored at index 0, while the last element is stored at index n-1, where n is the total number of elements in the array. </p>
                <p>In the case of the age array, the first element will be stored at index 0, while the last element will be stored at index 4. </p>
                <p>Let us use the age array to demonstrate how array initialization can be done: </p>
                <pre className='output'>int age[5] = &#123;19, 18, 21, 20, 17&#125;;</pre>
                <p>The total number of elements within the { } cannot exceed the value stated within the [ ]. The element 19 is at index 0, 18 at index 1, 21 at index 2, 20 at index 3 and 17 at index 4. If you don't state the number of elements to be stored in the array within [ ], the array will only be large enough to hold the elements added within { }. For example: </p>
                <pre className='output'>int age[] = &#123;19, 18, 21, 20, 17&#125;;</pre>
                <p>The above statement will create exactly the same array as the previous one. You can also assign one element to an array using its index. For example: </p>
                <pre className='output'>age[3] = 20;</pre>
                <p>The above statement will store the value 20 at index 3 of the array named age. This means that 20 will be the 4<sup>th</sup> element of the array. </p>
                <h2>Types of Arrays</h2>
                <p>There are two types of C++ arrays: </p>
                <ul>
                    <li>One dimensional Array </li>
                    <li>Multi-dimensional Array</li>
                    <li>Pointer to an Array</li>
                </ul>
                <h3>One-Dimensional Array</h3>
                <p>This is an array in which the data items are arranged linearly in one dimension only. It is commonly called a 1-D array. Syntax: </p>
                <pre className='output'>datatype array-name[size];</pre>
                <ul>
                    <li>The array-name is the name of the array. </li>
                    <li>The size is the number of items to be stored in the array. </li>
                </ul>
                <p>For example: </p>
                <pre className='containers'>#include &lt;iostream&gt; <br />
using namespace std; <br />
 <br />
int main() <br />
&#123; <br />
	int age[5] = &#123; 19, 18, 21, 20, 17 &#125;; <br />
	for (int x = 0; x &lt; 5; x++) <br />
	&#123; <br />
		cout &lt;&lt;age[x]&lt;&lt;"\n"; <br />
	&#125; <br />
&#125; <br />
</pre>
<p><strong>Code Explanation:</strong> </p>
<ol>
    <li>Including the iostream header file in our code. It will allow us to read from and write to the console. </li>
    <li>Including the std namespace so as to use its classes and functions without calling it. </li>
    <li>Calling the main() function inside which the logic of the program should be added. </li>
    <li>Start of the body of the main() function. </li>
    <li>Declaring an array named age to store 5 integers. The 5 integers have also been initialized. </li>
    <li>Create an integer variable x using a for loop. </li>
    <li>The beginning of the body of the for loop. </li>
    <li>Using the loop variable x to iterate over the values of the array age and print them on the console. The "\n" is a newline character and prints in a new line after every iteration. </li>
    <li>End of the body of the for a loop.</li>
    <li>End of the body of the main() function.</li>
</ol>
<h3>Multi-dimensional Array</h3>
<p>This is an array in which data items are arranged to form an array of arrays. A multi-dimensional array can have any number of dimensions, but two-dimensional and three-dimensional arrays are common. Syntax: </p>
<pre className='output'>datatype array-name[d1][d2][d3]...[dn];</pre>
<p>The array-name is the name of the array that will have n dimensions. For example: </p>
<h3>Two Dimensional Array</h3>
<p>A 2D array stores data in a list with 1-D array. It is a matrix with rows and columns. To declare a 2D array, use the following syntax: </p>
<pre className='output'>type array-Name [ x ][ y ];</pre>
<p>The type must be a valid C++ data type. See a 2D array as a table, where x denotes the number of rows while y denotes the number of columns. This means that you identify each element in a 2D array using the form a[x][y], where x is the number of row and y the number of columns in which the element belongs. </p>
<p>Here is an example of how to initialize a 2D array: </p>
<div className='containers'>
<pre>
  <code className='keywords'>  int </code>a[2][3] = &#123;  <br />
   &#123;0, 2, 1&#125; ,   <code className='library'>  /*  row at index 0 */ </code><br />
   &#123;4, 3, 7&#125; ,   <code className='library'>  /*  row at index 1 */ </code><br />
   &#125;; <br />
</pre>
</div>
<p>In above example, we have a 2D array which can be seen as a 2x3 matrix. There are 2 rows and 3 columns. The element 0 can be accessed as a[0][1] because it is located at the intersection of row indexed 0 and column indexed 1. The element 3 can be accessed as a[1][2] because it is located at the intersection of row indexed 1 and column indexed 2. </p>
<p>Note that we simply added curly braces to differentiate the different rows of elements. The initialization could also have been done as follows: </p>
<div className='containers'>
<pre> #include &lt;iostream&gt;  <br/>
    using namespace std; <br/>
    int main()  <br/>
    &#123; <br/>
        // a 2x3 array <br/>
        int a[3][2] = &#123; &#123;0, 2&#125;, &#123;1, 4&#125;, &#123;3, 7&#125; &#125;; <br/>
     <br/>
        // traverse array elements <br/>
     <br/>
        for (int i=0; i&lt;3; i++) <br/>
            for (int j=0; j&lt;2; j++)  <br/>
             <br/>
            &#123; <br/>
                cout &lt;&lt; "a[" &lt;&lt;i&lt;&lt; "][" &lt;&lt;j&lt;&lt; "]: "; <br/>
                cout &lt;&lt; a[i][j] &lt;&lt; endl; <br/>
            &#125; <br/>
        return 0; <br/>
    &#125; <br/>
    </pre> <br/>
</div>
<p><strong>Code Explanation:</strong> </p>
<ol>
    <li>Including the iostream header file in our code. It will allow us to read from and write to the console. </li>
    <li>Including the std namespace so as to use its classes and functions without calling it. </li>
    <li>Calling the main() function within which code should be added. </li>
    <li>Start of the body of the main() function. </li>
    <li>A comment. The C++ compiler will skip this. </li>
    <li>Declaring a 2D array of 3 rows and 2 columns. Items have also been added to the array. </li>
    <li>A comment. The C++ compiler will skip this.</li>
    <li>Creating a variable i using a for a loop. This variable will iterate over the row indexes of the array. </li>
    <li>Creating a variable j using a for a loop. This variable will iterate over the column indexes of the array. </li>
    <li>Start of the body of the loops. </li>
    <li>Print the values of variables i and j on the console inside square brackets on the console. </li>
    <li>Print out the value stored at index [i][j] of the array a. </li>
    <li>End of the body of the loops. </li>
    <li>The main() function should return an integer value if the program runs fine. </li>
    <li>End of the body of the main() function. </li>
</ol>
<h3>Three –Dimensional Array</h3>
<p>A 3D array is an array of arrays. Each element in a 3D array is identified by a set of 3 indexes. To access the elements of a 3D array, we use three for loops. For example:</p>
<pre className='containers'>#include&lt;iostream&gt;  <br/>
    using namespace std; <br/>
    void main() <br/>
    &#123; <br/>
        int a[2][3][2] = &#123;4, 8&#125;,&#123;2, 4&#125;,&#123;1, 6&#125;, &#123;3, 6&#125;,&#123;5, 4&#125;,&#123;9, 3&#125;; <br/>
        cout &lt;&lt; "a[0][1][0] = " &lt;&lt; a[0][1][0] &lt;&lt; "\n"; <br/>
        cout &lt;&lt; "a[0][1][1] = " &lt;&lt; a[0][1][1] &lt;&lt; "\n"; <br/>
    &#125; <br/>
    </pre> <br/>
    <p><strong>Code Explanation:</strong> </p>
    <ol>
        <li>Including the iostream header file in our code. It will allow us to read from and write to the console. </li>
        <li>Including the std namespace so as to use its classes and functions without calling it. </li>
        <li>Calling the main() function inside which the logic of the program should be added. </li>
        <li>Start of body of the main() function. </li>
        <li>Declaring a 3D array named an of size 2x3x2. The values of the array have also been initialized. </li>
        <li>Accessing the item stored at index [0][1][0] of the array and printing it on the console. </li>
        <li>Accessing the item stored at index [0][1][1] of the array and printing it on the console. </li>
        <li>End of the body of the main() function.</li>
    </ol>
    <p>We will Discuss about Pointer to Array later on</p>
        </div>
    )
}

export default Arr
